import { OfferLettersService } from 'api-clients/monolith/services/OfferLettersService';
import { useCallback } from 'react';

import { MutationOptions, useApiServiceMutation } from 'hooks/useApiServiceMutation';

export type OfferData = Awaited<
  ReturnType<typeof OfferLettersService.postInternalApiApplicantsOfferLetters>
>;

type OfferOptions = MutationOptions<OfferData, unknown>;

export interface OfferValuesInput {
  offer_letter_template_id?: string;
  /** Template field key -> value. */
  field_values?: Record<string, string>;
  /**
   * Data key -> value, for the keys driving the template's conditional sections
   * (AXHE-4001). A separate map from `field_values` because it addresses a
   * different key space. Omitting the key on an update leaves the stored answers
   * alone; an empty object clears them.
   */
  condition_values?: Record<string, string>;
}

/** Create a draft offer + render its preview (AXHE-3718 create). */
export const useCreateOffer = (applicantExternalId: string, options?: OfferOptions) => {
  const run = useCallback(
    (input: OfferValuesInput) =>
      OfferLettersService.postInternalApiApplicantsOfferLetters(applicantExternalId, input),
    [applicantExternalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/** Edit an existing offer's values + re-render (AXHE-3718 update). */
export const useUpdateOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    (input: Omit<OfferValuesInput, 'offer_letter_template_id'>) =>
      OfferLettersService.patchInternalApiOfferLetters(externalId, input),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};

/** Submit per the resolved CTA — send-for-approval vs send-to-candidate. */
export const useSubmitOffer = (externalId: string, options?: OfferOptions) => {
  const run = useCallback(
    () => OfferLettersService.postInternalApiOfferLettersSubmitForApproval(externalId),
    [externalId],
  );

  return useApiServiceMutation<OfferData, typeof run>(run, options);
};
